import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { flatMap, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { assertNotNull } from '@core/prelude';
import { ContactSummary } from '@features/contacts/models';
import { Trust } from '@features/contacts/models/trust';
import { ContactService } from '@features/contacts/services/contact.service';
import { TrustService } from '@features/contacts/services/trust.service';
import { ModalSetting } from '@shared/components/modal/modal-setting';

import { TagEntityType } from '@features/opportunities/services/tag.service';
import { TrustEditComponent } from '../details/edit/trust-edit.component';

export type LoadContent = {
  trust: Trust.Model;
  contacts: ContactSummary[];
};

@Component({
  selector: 'spark-contacts-trust-summary',
  templateUrl: './trust-summary.component.html',
  styleUrls: ['./trust-summary.component.scss']
})
export class TrustSummaryComponent {
  TagEntityType = TagEntityType;
  @Input() refresh$: BehaviorSubject<null> = new BehaviorSubject<null>(null);

  trustId!: string;
  contacts: ContactSummary[] = [];

  private companyId$: Observable<string> = this.route.paramMap.pipe(
    map((params) => params.get('trustId')),
    assertNotNull(
      'Internal error: TrustSummaryComponent expects trustId parameter in route'
    ),
    tap((id) => (this.trustId = id)),
    shareReplay(1)
  );

  load$: Observable<LoadContent> = combineLatest([
    this.refresh$,
    this.companyId$
  ]).pipe(
    switchMap(([_, trustId]) =>
      this.trustService.getTrustForDisplay(trustId).pipe(
        switchMap((trust) => {
          let contactIds: string[] = [];
          trust.trustees.forEach((item) => contactIds.push(item));
          trust.beneficiaries.forEach((item) => contactIds.push(item.id));

          return forkJoin({
            trust: of(trust),
            contacts: this.contactService
              .getSpecificContacts(contactIds)
              .pipe(tap((x) => (this.contacts = x)))
          });
        })
      )
    )
  );

  private modal: BsModalRef | null = null;

  constructor(
    private modalService: BsModalService,
    private route: ActivatedRoute,
    private trustService: TrustService,
    private contactService: ContactService
  ) {}

  public contactName(id: string) {
    let contact = this.contacts.find((x) => x.id == id);
    return contact ? contact.name : '';
  }

  public edit(): void {
    this.openFormModal(TrustEditComponent, this.trustId);
  }

  private closeModal(): void {
    this.modal?.hide();
  }

  private openFormModal(component: any, trustId: string | null = null) {
    this.closeModal();
    let content: any = {
      cancel: () => this.closeModal(),
      saved: () => this.closeAndRefresh()
    };

    if (trustId) {
      content.trustId = trustId;
    }

    this.modal = this.modalService.show(
      component,
      ModalSetting.createLargeModal(content)
    );
  }

  private closeAndRefresh(): void {
    this.closeModal();
    this.refresh$.next(null);
  }
}
